import { createGlobalStyle } from 'styled-components';
import '@fontsource/poppins';
import '@fontsource/inter';

const GlobalStyles = createGlobalStyle`
  :root {
    /* Primary Colors */
    --primary: #00E6FF; /* Tigers Blue */
    --primary-dark: #00B2CC;
    --primary-light: #33EEFF;
    --primary-rgb: 0, 230, 255;

    /* Secondary Colors */
    --secondary: #000000; /* Tiger Black */
    --secondary-light: #333333;
    --secondary-dark: #000000;

    /* Text Colors */
    --text: #0F172A;
    --text-light: #475569;
    --gray: #64748B;
    --gray-light: #CBD5E1;
    
    /* Background Colors */
    --background: #F8FAFC;
    --white: #FFFFFF;
    
    /* Accent Colors */
    --accent: #FFFFFF; /* Ice White */
    --accent-light: #F0F9FF;
    --accent-dark: #E6E6E6;
    --error: #dc3545;
    --success: #28a745;
    
    /* Gray Scale */
    --gray-50: #F8FAFC;
    --gray-100: #F1F5F9;
    --gray-200: #E2E8F0;
    --gray-300: #CBD5E1;
    --gray-400: #94A3B8;
    --gray-500: #64748B;
    --gray-600: #475569;
    --gray-700: #334155;
    --gray-800: #1E293B;
    --gray-900: #0F172A;
    
    /* Gradients */
    --gradient-primary: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
    --gradient-dark: linear-gradient(135deg, var(--secondary) 0%, var(--secondary-light) 100%);
    --gradient-ice: linear-gradient(135deg, var(--accent) 0%, var(--primary-light) 100%);
    
    /* Typography */
    --font-primary: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    --font-secondary: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    
    /* Spacing */
    --space-1: 0.25rem;
    --space-2: 0.5rem;
    --space-3: 0.75rem;
    --space-4: 1rem;
    --space-5: 1.25rem;
    --space-6: 1.5rem;
    --space-8: 2rem;
    --space-10: 2.5rem;
    --space-12: 3rem;
    --space-16: 4rem;
    
    /* Border Radius */
    --radius-sm: 0.25rem;
    --radius-md: 0.375rem;
    --radius-lg: 0.5rem;
    --radius-xl: 0.75rem;
    --radius-2xl: 1rem;
    --radius-full: 9999px;
    
    /* Shadows */
    --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
    
    /* Transitions */
    --transition-speed: 0.2s;
    --transition-speed-slow: 0.3s;
    --transition-bounce: cubic-bezier(0.34, 1.56, 0.64, 1);
    
    /* Effects */
    --hover-lift: translateY(-2px);
    --hover-lift-sm: translateY(-1px);
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: var(--font-primary);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: var(--background);
    color: var(--text);
    line-height: 1.5;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-primary);
    font-weight: 600;
    line-height: 1.2;
  }

  a {
    text-decoration: none;
    color: inherit;
    transition: all var(--transition-speed) ease;
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: var(--font-primary);
    transition: all var(--transition-speed) ease;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  /* Modern Scrollbar */
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: var(--gray-100);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--gray-300);
    border-radius: var(--radius-full);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--gray-400);
  }
`;

export default GlobalStyles; 